// src/games/darkforest/scenes/forest_entry.js
import { DIFFICULTY_CLASS } from '../../../game-system/skills/skillSystem';

export const forest_entry = {
  intro: {
    id: 'intro',
    title: "Edge of the Dark Forest",
    description: `You stand at the edge of the legendary Dark Forest, a place of mystery and danger. Ancient trees tower above you, their thick canopy blocking much of the sunlight. Strange sounds echo from deep within, and a cool mist hangs in the air.

Local villagers have offered a reward for anyone brave enough to venture into the forest and retrieve a mysterious crystal said to be hidden at an ancient shrine. They've warned of strange creatures, old magic, and those who entered and never returned.

The worn path before you leads into the shadowy depths. This is your last chance to prepare before the journey begins.`,
    choices: [
      {
        id: 'enter_forest',
        text: "Step onto the forest path",
        nextScene: 'forest_entrance',
        requires: null
      },
      {
        id: 'check_supplies',
        text: "Check your supplies",
        nextScene: 'check_inventory',
        requires: null
      },
      {
        id: 'listen_rumors',
        text: "Recall the rumors you've heard about the forest",
        nextScene: 'forest_rumors',
        requires: null
      }
    ]
  },
  
  check_inventory: {
    id: 'check_inventory',
    title: "Checking Your Supplies",
    description: `You take a moment to check your pack and make sure you have everything you need for the journey ahead. You have:

- A waterskin filled with fresh water
- Enough rations for a few days
- A small knife
- A flint and steel for making fire
- A small coil of rope

It's not much, but it should be enough to get you started. You might find more useful items along the way.`,
    choices: [
      {
        id: 'enter_forest',
        text: "Ready to enter the forest",
        nextScene: 'forest_entrance',
        requires: null
      },
      {
        id: 'listen_rumors',
        text: "Recall the rumors you've heard about the forest",
        nextScene: 'forest_rumors',
        requires: null
      }
    ]
  },
  
  forest_rumors: {
    id: 'forest_rumors',
    title: "Forest Rumors",
    description: `You recall the stories you've heard about the Dark Forest:

The elders speak of an ancient civilization that once lived within the forest, leaving behind ruins and artifacts of great power. They say the shrine you seek was a place of worship for these people.

Hunters tell of strange beasts that roam the deepest parts of the woods, creatures unlike any found elsewhere. They move silently and strike without warning.

A traveling mage mentioned that the forest has places where magic flows strongly, causing unusual phenomena and empowering magical artifacts.

The village healer warned of poisonous plants with colorful berries and mushrooms that cause hallucinations or worse.

Most concerning are the tales of people entering the forest and becoming lost, wandering for days only to emerge miles from where they entered, with no memory of what happened.`,
    choices: [
      {
        id: 'enter_forest',
        text: "You've heard enough - time to enter the forest",
        nextScene: 'forest_entrance',
        requires: null
      },
      {
        id: 'check_supplies',
        text: "Check your supplies before entering",
        nextScene: 'check_inventory',
        requires: null
      }
    ]
  },
  
  forest_entrance: {
    id: 'forest_entrance',
    title: "Into the Dark Forest",
    description: `You step onto the narrow path leading into the Dark Forest. The temperature drops noticeably as you pass beneath the ancient trees, and the sounds of the outside world fade away, replaced by the gentle rustling of leaves and distant animal calls.

Dappled sunlight filters through the dense canopy, casting shifting patterns on the forest floor. The path ahead is visible but grows narrower as it winds deeper into the woods.

After walking for a short time, you notice a weathered stone marker beside the path. Moss covers most of it, but some sort of inscription might be visible underneath.`,
    choices: [
      {
        id: 'continue_path',
        text: "Continue along the path",
        nextScene: 'forest_path_fork',
        requires: null
      },
      {
        id: 'examine_marker',
        text: "Examine the stone marker",
        nextScene: 'stone_marker',
        requires: null
      },
      {
        id: 'look_surroundings',
        text: "Search the surrounding area",
        nextScene: 'forest_entrance_search',
        requires: null
      }
    ],
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: "Notice details about your surroundings",
        results: {
          success: "You notice subtle signs that someone or something has passed this way recently. There are broken twigs and disturbed undergrowth just off the path.",
          failure: "Nothing particularly unusual catches your eye."
        }
      }
    ]
  },
  
  stone_marker: {
    id: 'stone_marker',
    title: "Ancient Stone Marker",
    description: `You brush away the moss covering the stone marker, revealing a worn inscription and some kind of symbol carved into the rock. The writing is in an old language, but parts of it seem vaguely familiar.`,
    skillChecks: [
      {
        skill: 'arcana',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Decipher the arcane markings",
        results: {
          success: "You recognize this as an old boundary marker with protective enchantments. The symbol represents safe passage, and the inscription roughly translates to 'The forest watches the worthy and misleads the unworthy.'",
          failure: "The markings appear to be magical in nature, but you can't determine their specific purpose or meaning."
        }
      },
      {
        skill: 'history',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Recall historical knowledge about these markers",
        results: {
          success: "You recognize this as a wayfinder stone from the ancient Sylvan civilization that once inhabited these woods. These markers were placed at the entrance to their territories and along important paths, serving both as guides and warnings.",
          failure: "You can tell this marker is very old, possibly hundreds of years, but you don't recognize its specific origin or purpose."
        }
      }
    ],
    choices: [
      {
        id: 'continue_path',
        text: "Continue along the path",
        nextScene: 'forest_path_fork',
        requires: null
      },
      {
        id: 'look_surroundings',
        text: "Search the surrounding area",
        nextScene: 'forest_entrance_search',
        requires: null
      }
    ]
  },
  
  forest_entrance_search: {
    id: 'forest_entrance_search',
    title: "Searching the Forest Entrance",
    description: `You carefully explore the area around the forest entrance, looking for anything of interest.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Search thoroughly for hidden items or clues",
        results: {
          success: "Hidden under some fallen leaves near the base of a large tree, you find a small leather pouch. Inside is a rusty dagger and a small, tightly folded piece of parchment with a crude map of what appears to be part of the forest.",
          rewards: {
            items: ['rusty_dagger', 'forest_map'],
            experience: 25
          },
          failure: "Despite your careful search, you don't find anything particularly useful."
        }
      },
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: "Look for signs of recent passage or wildlife",
        results: {
          success: "You notice various animal tracks nearby, including deer and rabbits. There are also human bootprints on the path, probably a few days old, heading into the forest but not coming back out.",
          experience: 15,
          failure: "You see the usual signs of forest wildlife, but nothing particularly noteworthy."
        }
      }
    ],
    choices: [
      {
        id: 'continue_path',
        text: "Continue along the path",
        nextScene: 'forest_path_fork',
        requires: null
      },
      {
        id: 'examine_marker',
        text: "Examine the stone marker",
        nextScene: 'stone_marker',
        requires: null
      }
    ]
  }
};